import React, { isValidElement, useState, useEffect, useLayoutEffect, useRef, } from 'react';
export var PatternChar;
(function (PatternChar) {
    PatternChar["Any"] = "*";
    PatternChar["Number"] = "9";
    PatternChar["Letter"] = "A";
})(PatternChar || (PatternChar = {}));
const patternChars = Object.values(PatternChar);
const RegExps = {
    Letters: /^[a-z]/i,
    Numbers: /^[0-9]/i,
};
export function format(value, pattern) {
    const cleanedValue = value.replace(/[^a-z0-9]/gi, ''); // remove all special chars
    const chars = cleanedValue.split('');
    let count = 0;
    let formatted = '';
    for (var i = 0; i < pattern.length; i++) {
        const patternChar = pattern[i];
        const char = chars[count];
        if (char && patternChar) {
            if (patternChar === PatternChar.Any) {
                // Any letter or number
                formatted += char;
                count++;
            }
            else if (patternChar === PatternChar.Number) {
                // Numbers only
                if (RegExps.Numbers.test(char)) {
                    formatted += char;
                }
                count++;
            }
            else if (patternChar === PatternChar.Letter) {
                // Letters only
                if (RegExps.Letters.test(char)) {
                    formatted += char;
                }
                count++;
            }
            else {
                formatted += patternChar;
            }
        }
    }
    return formatted;
}
/**
 * Simple input mask that accepts a user defined pattern and reformats any text entered into it.
 *
 * Use "A" to represent a letter
 * Use "9" to represent a number
 * Use "*" to represent either a letter OR a number
 *
 * Example: A-9999_****
 *
 * When using TxInputMask with a forms library like Formik or react-hook-forms
 * you should pass the "value" and "onChange" props to the input mask itself and everything else to the wrapped input
 */
export function TxInputMask({ children, onChange, value, mask, }) {
    const [prevState, setPrevState] = useState('');
    const [state, setState] = useState('');
    const [selectionStart, setSelectionStart] = useState(0);
    const internalRef = useRef(null);
    // Ensure that the internalRef is correctly passed down to the child component
    useEffect(() => {
        const childRef = children.ref;
        if (internalRef.current && childRef) {
            if (typeof childRef === 'function') {
                childRef(internalRef.current);
            }
            else {
                childRef.current = internalRef.current;
            }
        }
    }, [children.ref]);
    // The selection range needs to be set after React is done rendering
    useLayoutEffect(() => {
        const offset = state.length - prevState.length; // offset lets you find out how many charcter have been added from the defined pattern
        if (internalRef.current) {
            const maskChar = mask[selectionStart - 1];
            // check if the current value is not a space for characters and has an offset greater then 0
            if (maskChar && !patternChars.includes(maskChar) && offset > 0) {
                internalRef.current.setSelectionRange(selectionStart + offset, selectionStart + offset);
            }
            else {
                internalRef.current.setSelectionRange(selectionStart, selectionStart);
            }
        }
    }, [state, prevState, selectionStart]);
    // prevent entering information if an invalid character
    useEffect(() => {
        function onKeydown(e) {
            if (!e.key) {
                return;
            }
            const offset = state.length - prevState.length; // offset lets you find out how many charcter have been added from the defined pattern
            const patternChar = offset < 0 ? mask[selectionStart] : mask[selectionStart - offset];
            if (e.key.length === 1 && /^[a-z0-9]/i.test(e.key) && patternChar) {
                if (state.length >= mask.length) {
                    e.preventDefault();
                }
                else if (patternChar === PatternChar.Number) {
                    if (!RegExps.Numbers.test(e.key)) {
                        e.preventDefault();
                    }
                }
                else if (patternChar === PatternChar.Letter) {
                    // Letters only
                    if (!RegExps.Letters.test(e.key)) {
                        e.preventDefault();
                    }
                }
            }
        }
        if (internalRef.current) {
            internalRef.current.addEventListener('keydown', onKeydown);
        }
        return () => {
            if (internalRef.current) {
                internalRef.current.removeEventListener('keydown', onKeydown);
            }
        };
    }, [internalRef, selectionStart, mask, state, prevState]);
    // make sure that value is formatted when new prop is passed
    useEffect(() => {
        if (value) {
            setState(format(value, mask));
        }
    }, [value]);
    function internalOnChange(e) {
        const selectionStart = e.target.selectionStart;
        const value = format(e.target.value, mask);
        if (selectionStart !== null) {
            setSelectionStart(selectionStart); // track the previous selection start
        }
        setPrevState(state); // store previous value
        setState(value); // store new formatted value
        if (onChange) {
            onChange(e);
        }
    }
    if (isValidElement(children)) {
        // Specify the expected props for the cloned element
        const clonedChild = React.cloneElement(children, {
            value: state,
            onChange: internalOnChange,
            ref: internalRef,
        });
        return clonedChild;
    }
    return React.createElement(React.Fragment, null);
}
//# sourceMappingURL=InputMask.js.map